import { useState, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';
import { getApiBaseUrl, getSocketIoPath } from '../../../../core/config/api';
import { type LiveVehicleTrack } from '../../components/WorldMap';
import {
  type InstanceRow,
  mergeInterventionRow,
} from '../../components/interventions/instanceModel';

interface UseMapSocketParams {
  userId: number | undefined;
  setInterventions: React.Dispatch<React.SetStateAction<InstanceRow[]>>;
  setPriseAppels: (value: boolean) => void;
  loadInterventions: () => Promise<void>;
}

export function useMapSocket({
  userId,
  setInterventions,
  setPriseAppels,
  loadInterventions,
}: UseMapSocketParams) {
  const [liveVehicleTracks, setLiveVehicleTracks] = useState<LiveVehicleTrack[]>([]);

  const loadLiveVehicles = useCallback(async () => {
    if (!userId) {
      setLiveVehicleTracks([]);
      return;
    }
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      if (!token) return;
      const res = await fetch(
        `${getApiBaseUrl()}/api/cta/interventions-instance/live-vehicles`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      const data = await res.json().catch(() => ({}));
      if (data?.success && Array.isArray(data?.vehicles)) {
        setLiveVehicleTracks(data.vehicles as LiveVehicleTrack[]);
      }
    } catch {
      setLiveVehicleTracks([]);
    }
  }, [userId]);

  const applyLiveVehiclesDelta = useCallback(
    (data: {
      mode?: 'full' | 'delta';
      vehicles?: LiveVehicleTrack[];
      upserts?: LiveVehicleTrack[];
      removed_keys?: string[];
    }) => {
      const keyOf = (t: LiveVehicleTrack) =>
        `${Number(t.intervention_id)}:${Number(t.vehicle_id)}`;
      if (data.mode === 'full' && Array.isArray(data.vehicles)) {
        setLiveVehicleTracks(data.vehicles);
        return;
      }
      if (data.mode === 'delta') {
        setLiveVehicleTracks((prev) => {
          const map = new globalThis.Map<string, LiveVehicleTrack>();
          for (const p of prev) map.set(keyOf(p), p);
          for (const u of data.upserts || []) map.set(keyOf(u), u);
          for (const rk of data.removed_keys || []) map.delete(String(rk));
          return Array.from(map.values());
        });
        return;
      }
      if (Array.isArray(data.vehicles)) setLiveVehicleTracks(data.vehicles);
    },
    [],
  );

  useEffect(() => {
    if (!userId) return;

    const ownerOf = (payload: InstanceRow): number | undefined => {
      if (payload.joueur_id != null && Number.isFinite(Number(payload.joueur_id)))
        return Number(payload.joueur_id);
      if (payload.user_id != null && Number.isFinite(Number(payload.user_id)))
        return Number(payload.user_id);
      return undefined;
    };

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    const socket = io(getApiBaseUrl(), {
      path: getSocketIoPath(),
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    const upsert = (row: InstanceRow) => {
      setInterventions((prev) => {
        const i = prev.findIndex((x) => x.id === row.id);
        if (i === -1) return [row, ...prev];
        const next = [...prev];
        next[i] = mergeInterventionRow(next[i], row);
        return next;
      });
    };

    socket.on('connect', () => {
      socket.emit('join:user', userId);
      void loadLiveVehicles();
    });

    socket.on('intervention:new', (payload: InstanceRow) => {
      if (ownerOf(payload) !== userId) return;
      if (payload?.id) upsert(payload);
      void loadLiveVehicles();
    });

    socket.on('intervention:update', (payload: InstanceRow) => {
      if (ownerOf(payload) !== userId) return;
      if (payload?.id) upsert(payload);
      void loadLiveVehicles();
    });

    socket.on('cta:live-vehicles:update', (data) => applyLiveVehiclesDelta(data || {}));

    socket.on('cta:prise-appels', (data: { prise_appels?: boolean }) => {
      if (typeof data?.prise_appels === 'boolean') setPriseAppels(data.prise_appels);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [userId, setInterventions, setPriseAppels, loadLiveVehicles, applyLiveVehiclesDelta]);

  useEffect(() => {
    if (!userId) { setLiveVehicleTracks([]); return; }
    void loadLiveVehicles();
  }, [userId, loadLiveVehicles]);

  return { liveVehicleTracks };
}
